import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { StoreDispatch } from "../store";
import { internalSearchFileContent, SearchFileContentRequest } from "src/network/graphql/bulkUploadService";
import {
  BulkUploadFileContent,
  BulkUploadFileContentListing,
  parseFileContentAttributes,
  parseFileContentContent
} from "../bulkUpload/bulkUploadTypes";
import { NOVATTI_DAILY_TRANSACTION } from "src/components/constant/constant";

// NOVATTI_DAILY_HOLD_TRANSACTIONS rows exist in storage but the AppSync FileType
// enum does not include the value yet, so only the daily-transaction type is queryable.
export const NOVATTI_FILE_CONTENT_TYPES = [NOVATTI_DAILY_TRANSACTION];

interface InternalNovattiFileContentsState {
  contentListLoading: boolean;
  contentListError: string | null;
  contents: BulkUploadFileContentListing;
}

const initialState: InternalNovattiFileContentsState = {
  contentListLoading: false,
  contentListError: null,
  contents: { list: [], total: 0 }
};

const internalNovattiFileContentsSlice = createSlice({
  name: "internalNovattiFileContents",
  initialState,
  reducers: {
    fetchContentsStart: (state) => {
      state.contentListLoading = true;
      state.contentListError = null;
    },
    fetchContentsSuccess: (state, action: PayloadAction<BulkUploadFileContentListing>) => {
      state.contentListLoading = false;
      state.contents = action.payload;
    },
    fetchContentsFail: (state, action: PayloadAction<string>) => {
      state.contentListLoading = false;
      state.contentListError = action.payload;
      state.contents = { list: [], total: 0 };
    }
  }
});

export const { fetchContentsStart, fetchContentsSuccess, fetchContentsFail } =
  internalNovattiFileContentsSlice.actions;

const toErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return "Something went wrong";
};

export const fetchInternalNovattiFileContentsAction = (data: SearchFileContentRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchContentsStart());
    try {
      const response = await internalSearchFileContent({
        ...data,
        filter: { ...data.filter, file_types: NOVATTI_FILE_CONTENT_TYPES }
      });
      const payload = response.data?.internal_search_file_content ?? {};
      const rawList: BulkUploadFileContent[] = payload.file_content_list ?? [];

      const normalised = rawList.map((row) => ({
        ...row,
        attributes: parseFileContentAttributes(row.attributes),
        content: parseFileContentContent(row.content)
      }));

      dispatch(
        fetchContentsSuccess({
          list: normalised,
          total: payload.total_size ?? normalised.length
        })
      );
    } catch (error) {
      dispatch(fetchContentsFail(toErrorMessage(error)));
    }
  };
};

export default internalNovattiFileContentsSlice.reducer;
